import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DisplayGuestDto, DisplayUserDto, GuestWatcherDto, UserWatcherDto, WatcherDto } from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { splitName } from '../lib/split-name';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GalleryPasswordService } from './gallery-password.service';
import { StateService } from './state.service';

interface InternalWatcher {
  id: string;
  assetId?: string;
  galleryId: string;
}

interface Watcher {
  id: string;
  asset?: string;
  gallery?: string;
  user?: string;
}

export interface WatchingPerson {
  id: string;
  first_name: string;
  last_name: string;
  initials: string;
  avatar_url?: string;
  anonymous_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WatcherService {
  constructor(
    private config: AppConfigService,
    private http: HttpClient,
    private loader: DataLoaderService,
    private state: StateService,
    private galleryPasswordService: GalleryPasswordService
  ) {}

  public async getMySubscriptions(bustCache = false) {
    if (bustCache) this.loader.remove('mySubscriptions');
    const resp = await this.loader.get('mySubscriptions', () => this.getMySubscriptionsInternal());
    return resp;
  }

  private async getMySubscriptionsInternal(results: Watcher[] = [], page = 0): Promise<InternalWatcher[]> {
    const resp = await lastValueFrom(
      this.http.get<{ data: Watcher[] }>(`${this.config.apiEndpoint}/watchers?limit=100&page=${page}`)
    );
    results = [...results, ...resp.data];
    if (resp.data.length === 100) return this.getMySubscriptionsInternal(results, page + 1);

    return results.map(item => ({
      id: item.id,
      galleryId: item.gallery,
      assetId: item.asset,
    }));
  }

  public async getAssetWatchers(assetId: string, galleryId: string): Promise<WatchingPerson[]> {
    const [assetWatchers, galleryWatchers] = await Promise.all([
      this.internalGetAssetWatchers(assetId, galleryId),
      this.internalGetGalleryWatchers(galleryId),
    ]);
    const list: WatcherDto[] = assetWatchers;

    // Merge gallery watchers into asset watchers because they are also watching the asset
    galleryWatchers.forEach(watcher => {
      if ('user' in watcher || 'guest' in watcher) {
        if ('user' in watcher) {
          if (watcher.user && list.find(i => (<UserWatcherDto>i).user?.id === watcher.user?.id)) return;
        } else {
          if (watcher.guest && list.find(i => (<GuestWatcherDto>i).guest?.id === watcher.guest?.id)) return;
        }
      }
      list.push(watcher);
    });

    const people: (DisplayUserDto | DisplayGuestDto)[] = list.map(watcher =>
      'user' in watcher ? watcher.user : watcher.guest
    );

    const mappedPeople: WatchingPerson[] = [];

    people.forEach(watcher => {
      if (!watcher) return;
      const first_name = 'first_name' in watcher ? watcher.first_name : this.getFirstName(watcher);
      const last_name = 'last_name' in watcher ? watcher.last_name : this.getLastName(watcher);

      const mappedWatcher: WatchingPerson = {
        first_name,
        last_name,
        initials: this.getInitials(first_name, last_name),
        id: watcher.id,
      };

      if ('name' in watcher) {
        mappedWatcher.anonymous_id = watcher.anonymous_id;
      } else {
        mappedWatcher.avatar_url = watcher.avatar_url;
      }
      mappedPeople.push(mappedWatcher);
    });

    return mappedPeople;
  }

  private getFirstName(watcher: DisplayGuestDto) {
    const { first_name } = splitName(watcher.name);
    return first_name;
  }

  private getLastName(watcher: DisplayGuestDto) {
    const { last_name } = splitName(watcher.name);
    return last_name;
  }

  private getInitials(first_name: string, last_name: string): string {
    let initials = first_name[0];
    if (last_name) initials += last_name[0];
    return initials;
  }

  public async watchAsset(assetId: string, galleryId: string) {
    if (this.state.guest && !this.state.guest.email) return;
    const items = await this.getMySubscriptions();
    if (items.find(item => item.galleryId === galleryId)) return;
    if (items.find(item => item.assetId === assetId)) return;
    this.loader.remove('mySubscriptions');
    return lastValueFrom(
      this.http.post(`${this.config.apiEndpoint}/watchers${this.getPassword(galleryId)}`, { asset: assetId })
    );
  }

  public async watchGallery(galleryId: string) {
    if (this.state.guest && !this.state.guest.email) return;
    const items = await this.getMySubscriptions();
    if (items.find(_item => _item.galleryId === galleryId)) return console.warn('Already subscribed');
    this.loader.remove('mySubscriptions');
    return lastValueFrom(
      this.http.post(`${this.config.apiEndpoint}/watchers${this.getPassword(galleryId)}`, { gallery: galleryId })
    );
  }

  public async unWatchGallery(galleryId: string) {
    const items = await this.getMySubscriptions();
    const _items = items.filter(item => item.galleryId === galleryId);
    if (!_items.length) return console.warn('Gallery subscription not found');
    this.loader.remove('mySubscriptions');
    await Promise.all(
      _items.map(item => lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/watchers/${item.id}`)))
    );
  }

  public async unWatchAsset(assetId: string) {
    const items = await this.getMySubscriptions();
    const item = items.find(_item => _item.assetId === assetId);
    if (!item) return console.warn('Asset subscription not found');
    this.loader.remove('mySubscriptions');
    return lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/watchers/${item.id}`));
  }

  private async internalGetAssetWatchers(assetId: string, galleryId: string) {
    const url = `${this.config.apiEndpoint}/assets/${assetId}/watchers${this.getPassword(galleryId)}`;
    const resp = <{ data: WatcherDto[] }>await lastValueFrom(this.http.get(url));
    return resp.data;
  }

  private async internalGetGalleryWatchers(galleryId: string) {
    const url = `${this.config.apiEndpoint}/galleries/${galleryId}/watchers${this.getPassword(galleryId)}`;
    const resp = <{ data: WatcherDto[] }>await lastValueFrom(this.http.get(url));
    return resp.data;
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '?password=' + encodeURIComponent(password);
  }
}
